import { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Spinner } from '@blocket/blocket-ui'
import { useIntersectionObserver } from 'customHooks/useIntersectionObserver'
import { imageLoad } from 'utils/image'

const CarouselItem = styled.div`
  padding: 0 20px;
  position: relative;
  display: flex;
  flex: 1 0 100%;
  scroll-snap-align: start;
`

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  ${({ blur }) => blur && `filter: blur(8px);`}
`

const SpinnerWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`

const Item = ({ src, thumbnail = '', alt = '', onImageIntersect = () => {} }) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [isLoading, setIsLoading] = useState(null)
  const imageRef = useRef([])

  useIntersectionObserver(
    imageRef,
    () => {
      onImageIntersect()
    },
    { threshold: 0.8 },
  )

  const doneLoading = useCallback(() => {
    setImageSrc(src)
    setIsLoading(false)
  }, [src])

  useEffect(() => {
    setIsLoading(true)

    if (thumbnail) {
      imageLoad({
        src: thumbnail,
        onLoad: () => {
          setImageSrc(thumbnail)
          imageLoad({
            src,
            onLoad: () => {
              doneLoading()
            },
          })
        },
      })
    } else if (!thumbnail && src) {
      imageLoad({
        src,
        onLoad: () => {
          doneLoading()
        },
      })
    }
  }, [doneLoading, src, thumbnail])

  return (
    <CarouselItem data-testid="carousel-item">
      {isLoading && (
        <SpinnerWrapper data-testid="carousel-item-spinner">
          <Spinner white />
        </SpinnerWrapper>
      )}
      <CarouselImage ref={imageRef} src={imageSrc} alt={alt} blur={isLoading} />
    </CarouselItem>
  )
}

Item.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  thumbnail: PropTypes.string,
  onImageIntersect: PropTypes.func,
}

export default Item
